/* @flow */

import type { KeyValuePair } from '../../../../helpers/jsHelpers/types';
import type { BO_API_PARAMETERS_TYPE } from '../../../../helpers/videofutur/queryParameters';
import type { BO_RESPONSE_WITH_ETAG } from '../../../../redux/netgemApi/actions/videofutur/types/common';
import type { NETGEM_API_V8_REQUEST_HEADERS } from '../../v8/types/Headers';

import { XhrResponseType } from '../../../../helpers/jsHelpers/xhr';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import { type CommonPromisedXMLHttpRequestReturnType, commonNetworkErrorCallBack, commonPromisedXMLHttpRequest, getETagFromRequest } from '../../helpers/CommonPromisedXMLHttpRequest';
import { CustomNetworkError } from '../../helpers/CustomNetworkError';
import { HeaderName } from '../../v8/constants/Headers';
import { HttpMethod } from '../../v8/types/HttpMethod';
import { BO_INVALID_APPLICATION_V2, BO_INVALID_JWT_V2, BO_INVALID_SUBSCRIBER_V2, BO_STREAM_DEVICE_NOT_FOUND } from '../types/ErrorCodes';

const buildUrl: (url: string, parameters: BO_API_PARAMETERS_TYPE) => string = (url, parameters) => {
  let urlWithParameters = url;

  Object.entries(parameters).forEach(([key, value]) => {
    urlWithParameters = urlWithParameters.replace(`{${key}}`, typeof value === 'string' ? value : '');
  });

  return urlWithParameters;
};

const sendBORequest: (
  baseUrl: string,
  method: HttpMethod,
  authenticationToken: string | null,
  body: ?KeyValuePair<string | number | boolean>,
  headers: ?NETGEM_API_V8_REQUEST_HEADERS,
  signal?: AbortSignal,
  keepAlive?: boolean,
  eTag?: string,
) => Promise<BO_RESPONSE_WITH_ETAG> = (baseUrl, method, authenticationToken, body, headers, signal, keepAlive, eTag) => {
  // Add authentication token
  const headerList = headers ?? [];

  if (authenticationToken !== null) {
    // Add authentication token if present
    headerList.push({
      name: HeaderName.Authorization,
      value: `Bearer ${authenticationToken}`,
    });
  }

  if (eTag) {
    headerList.push({
      name: !method || method === HttpMethod.GET ? HeaderName.IfNoneMatch : HeaderName.IfMatch,
      value: eTag,
    });
  }

  const returnObject: CommonPromisedXMLHttpRequestReturnType = commonPromisedXMLHttpRequest(
    baseUrl,
    method,
    XhrResponseType.Json,
    headers,
    body ? JSON.stringify(body) : null,
    null,
    false,
    commonNetworkErrorCallBack,
    signal,
    keepAlive,
  );

  return returnObject.promise
    .then((response: any) => {
      const { xhr } = returnObject;
      const newETag = getETagFromRequest(xhr);

      return Promise.resolve({ eTag: newETag, response });
    })
    .catch((error: CustomNetworkError) => {
      const code = error.getCustomCode();
      if (code === BO_STREAM_DEVICE_NOT_FOUND || code === BO_INVALID_JWT_V2 || code === BO_INVALID_APPLICATION_V2 || code === BO_INVALID_SUBSCRIBER_V2) {
        // Token expired (device probably disconnected)
        Messenger.emit(MessengerEvents.AUTHENTICATION_REQUIRED);
      }

      throw error;
    });
};

export { buildUrl, sendBORequest };
